import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNaves } from '@/lib/queries/naves';
import { useSensoresNaves } from '@/lib/queries/iot';
import { hasSensorAlert, sensorAlertLabel, sensorStationName } from '@/lib/iotPresentation';
import { fmtFechaHora } from '@/lib/format';

export function NaveNotifications({ onNavigate }: { onNavigate: () => void }) {
  const navesQ = useNaves();
  const denied = [401, 403].includes((navesQ.error as { status?: number } | null)?.status ?? 0);
  const naves = denied ? [] : (navesQ.data ?? []);
  const queries = useSensoresNaves(naves.map((n) => n.id));
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const changed = () => setOffline(!navigator.onLine);
    window.addEventListener('online', changed);
    window.addEventListener('offline', changed);
    return () => {
      window.removeEventListener('online', changed);
      window.removeEventListener('offline', changed);
    };
  }, []);
  const loading = navesQ.isLoading || queries.some((q) => q.isLoading);
  const error = navesQ.isError || queries.some((q) => q.isError);
  const complete = !!navesQ.data && !denied && queries.every((q) => q.data != null);
  const notices = naves
    .map((nave, i) => {
      const data = queries[i].data;
      return {
        nave,
        alerts: [
          ...(data?.environment ?? []).flatMap((device, index) =>
            [device.temperature, device.humidity]
              .filter(hasSensorAlert)
              .map((reading) => ({ reading, station: device.station ?? sensorStationName(index) })),
          ),
          ...[data?.ammonia, data?.water, data?.silo]
            .filter(hasSensorAlert)
            .map((reading) => ({ reading, station: null })),
        ],
      };
    })
    .filter((n) => n.alerts.length);
  const cached = queries.some((q) => q.data?.local_snapshot);
  return (
    <div className="py-4">
      {(offline || cached) && (
        <p className="nave-caption" role="status">
          {cached ? 'Copia local · sin actualizar' : 'Sin conexión · avisos sin actualizar'}
        </p>
      )}
      {loading && <p role="status">Cargando avisos…</p>}
      {error && (
        <div role="alert" className="parte-error mb-3">
          No se pudieron actualizar todos los avisos. Sólo se muestran los disponibles.
          <button
            type="button"
            className="ui-secondary mt-3 w-full"
            disabled={navesQ.isFetching || queries.some((q) => q.isFetching)}
            onClick={() => {
              void navesQ.refetch();
              queries.forEach((q) => void q.refetch());
            }}
          >
            Reintentar
          </button>
        </div>
      )}
      {notices.length > 0 && (
        <>
          <p className="mb-3 font-medium">
            {notices.length} {notices.length === 1 ? 'nave con avisos' : 'naves con avisos'}
          </p>
          {(!complete || loading || error) && (
            <p className="nave-caption">Resultado parcial. Quedan avisos sin comprobar.</p>
          )}
          <ul className="divide-y divide-border">
            {notices.map(({ nave, alerts }) => (
              <li key={nave.id}>
                <Link
                  to={`/naves/${nave.id}?tab=sensores`}
                  onClick={onNavigate}
                  className="block min-h-12 py-3"
                >
                  <strong>{nave.nombre || `Nave ${nave.codigo}`}</strong>
                  {alerts.map(({ reading: r, station }, i) => (
                    <span key={i} className="block text-sm text-text-secondary">
                      {sensorAlertLabel(r)}
                      {station && ` · Estación ${station}`}
                      {r.received_at && ` · ${fmtFechaHora(r.received_at)}`}
                    </span>
                  ))}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
      {!loading && !error && complete && !notices.length && (
        <p className="text-text-secondary">
          {offline || cached
            ? 'Sin avisos en la copia conservada. Conecta para comprobar el estado actual.'
            : 'Sin avisos registrados.'}
        </p>
      )}
    </div>
  );
}
